import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Button, Dialog, NavBar, Skeleton, Toast} from 'antd-mobile';
import EmptyState from "../../../components/EmptyState";
import i18n, {t} from "../../../i18n";
import pickedUpDataService, {PickedUpData, uploadPhotosWithTimeout} from "../../../services/PickedUpDataService";
import {CheckCircleFill, PhoneFill} from "antd-mobile-icons";
import pickUpTopToast from "../components/PickUpTopToast";
import PickUpPhotoService, {
    PickUpPhotoType,
    PickUpPositionType,
    PICKUP_FILE_PHOTO_SLOTS
} from "../services/PickUpPhotoService";
import {PickUpNativePermissions} from "../components/PickUpPhotoNativeBridge";
import PickUpPhotoUploadCard from '../components/PickUpPhotoUploadCard';
import PickUpPhotoViewer from "../components/PickUpPhotoViewer";
import PickUpInfoItem from "../components/PickUpInfoItem";
import {PickupAppointmentType} from "../graphql/types/PickupTaskWithAppointmentsType";
import {buildAddressFromAppointment} from "../../../utils/addressUtils";
import {formatTimestampByLanguage} from "../../../utils/dateTimeUtils";
import {usePickupTaskAppointmentList, usePickupTaskOperations} from '../store/pickupStore';
import {pickupNavigationService} from "../services/PickupNavigationService";
import {parseExceptionMessageKey} from "../../../utils/exceptionUtils";
import {getNavBarContainerStyle, getBottomNavBarContainerStyle} from "../../../utils/deviceUtils";
import {navigateBack} from "../../../components/Map/MapNavigationUtils";

interface PackageHandoverPageProps {}

const MIN_HANDOVER_FILE_PHOTOS = 3;

const PackageHandoverPage: React.FC<PackageHandoverPageProps> = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const isPartialHandover = location?.state?.isPartialHandover;
    // 检查是否来自地图模式
    const fromMapMode = location.state?.fromMapMode || false;
    const [photos, setPhotos] = useState<Record<PickUpPositionType, PickUpPhotoType | undefined>>({} as Record<PickUpPositionType, PickUpPhotoType | undefined>);
    const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [pickingUpData, setPickingUpData] = useState<PickupAppointmentType>();
    const [nextItem, setNextItem] = useState<PickupAppointmentType | null>(null);
    const { pickupTaskAppointmentList, loading } = usePickupTaskAppointmentList();
    const { createUnloadRecord } = usePickupTaskOperations();

    useEffect(() => {
        if (pickupTaskAppointmentList.length > 0){
            const completeAppointments = pickupTaskAppointmentList;
            setPickingUpData(completeAppointments[completeAppointments?.length-1]);
        }
    }, [pickupTaskAppointmentList]);

    // 取第一站会触发列表刷新，不能依赖 pickupTaskAppointmentList，否则刷新产生新引用又触发本 effect，形成死循环
    useEffect(() => {
        if (isPartialHandover && !nextItem) {
            // 部分交接包裹场景，获取第一个揽收点
            pickupNavigationService.getFirstPickupItem().then(setNextItem);
        }
    }, [isPartialHandover]);

    useEffect(() => {
        const requestPermissions = async () => {
            try {
                await PickUpNativePermissions.request('camera');
                await PickUpNativePermissions.request('storage');
            } catch (error) {
                console.error('Failed to request permissions:', error);
            }
        };

        requestPermissions();
    }, []);

    // 保存订单数据到本地
    const savePickedUpToLocal = async (): Promise<boolean> => {

        try {
            const pickedUpData: PickedUpData = {
                updatedAt: Date.now(),
                photos: Object.entries(photos || {})
                    .map(([type, photo]) => ({
                    type: type as PickUpPositionType,
                    photo: photo as PickUpPhotoType
                })),
                id: pickingUpData?.id?.toString() || '',
                type: 'handover',
                stopNumber: pickingUpData?.sequence?.toString(),
                channelName: pickingUpData?.channelName,
                warehouseId: pickingUpData?.warehouseId,
                taskId: pickingUpData?.taskId,
                status: isPartialHandover === true ? 1 : 2
            };

            // 保存到本地
            const saved = await pickedUpDataService.savePickedUpData(pickedUpData);
            return saved;
        } catch (error) {
            console.error('Failed to save PickedUp to local:', error);
            return false;
        }
    };

    /*照片相关操作 start======*/

    // 拍照或从相册选择
    const takePhoto = async (type: PickUpPositionType, file: File) => {
        try {
            setIsLoading(true);
            await PickUpPhotoService.handlePhotoNotUpload(type, file, (updateFn) => {
                setPhotos((prev) => updateFn(prev));
            });
        } catch (error) {
            console.error('Failed to take photo:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // 删除照片
    const deletePhoto = (type: PickUpPositionType) => {
        Dialog.confirm({
            content:  t('pages.pickUp.photo.deleteContent'),
            confirmText: t('pages.pickUp.photo.deleteConfirm'),
            cancelText: t('pages.pickUp.photo.deleteCancel'),
            onConfirm: () => {
                PickUpPhotoService.handleDeletePhoto(type, (updateFn) => {
                    setPhotos((prev) => updateFn(prev));
                });
            },
        });
    };

    // 查看大图
    const handleViewPhoto = (url: string) => setViewingPhoto(url);

    /*照片相关操作 end======*/

    // 处理完成后的导航逻辑
    const handleNavigationAfterComplete = () => {
        if (nextItem && !fromMapMode) {
            // 如果不是从地图模式来的，且有下一条数据，跳转到下一条数据的详情页面
            navigate(`/pickup/picking-up/detail/${nextItem?.id}/${nextItem?.status}`);
        } else {
            if (fromMapMode) {
                navigateBack.navigateToLsatPage(navigate);//从地图模式来的返回地图页面
            } else {
                // 从列表模式来的，跳转到包裹列表页面
                navigateBack.navigateToPickupList(navigate);
                showPickUpToast()
            }
        }
    };

    const showPickUpToast = () => {
        pickUpTopToast.show({
            content: (
                <div className="flex items-center justify-center gap-2">
                    <CheckCircleFill className="text-2xl text-green-500"/>
                    <span className="text-xs">{t('pages.pickUp.packageHandover.pickUpCompleted')}</span>
                </div>
            ),
            position: 'top',
            duration: 3000
        });
    }

    // 完成交接包裹
    const handleCompletePackageHandover = async () => {
        setIsLoading(true);
        try {
            const selectedPhotos = PICKUP_FILE_PHOTO_SLOTS
                .map((slot) => photos[slot])
                .filter((item): item is PickUpPhotoType => item?.objectKey !== undefined && item?.url !== undefined);

            if (selectedPhotos.length < MIN_HANDOVER_FILE_PHOTOS) {
                Toast.show({ content: t('pages.pickUp.packageHandover.takeAtLeastPhotos'), position: 'center' });
                return;
            }

            const currentPhotos = {...photos};
            const status = isPartialHandover === true ? 1 : 2;
            //仅执行乐观提交逻辑，不进行网络请求
            await createUnloadRecord({
                taskId: pickingUpData?.taskId || '',
                warehouseId: pickingUpData?.warehouseId || '',
                status: status,
                photoS3Keys: selectedPhotos.map((photo) => photo.objectKey)
            },true);
            setPhotos(currentPhotos);

            // 图片上传，带超时控制
            const photoKeys = await uploadPhotosWithTimeout(selectedPhotos);

            //真正的调用接口
            await createUnloadRecord({
                taskId: pickingUpData?.taskId || '',
                warehouseId: pickingUpData?.warehouseId || '',
                status: status,
                photoS3Keys: photoKeys
            },false);

            handleNavigationAfterComplete();
        } catch (error: any) {
            if (error.networkError) {
                await savePickedUpToLocal();
                handleNavigationAfterComplete();
            } else {
                console.error('Failed to complete packageHandover:', error);
                const messageKey = parseExceptionMessageKey(error);
                if (messageKey === 'errors.pickup.unFinishedAppointment'){
                    Toast.show({ content: t(messageKey), position: 'center' });
                }else {
                    Toast.show({ content: 'Package handover failed, please try again', position: 'center' });
                }
            }
        } finally {
            setIsLoading(false);
        }
    };


    const handleCall = () => {
        if (!pickingUpData?.contactPhone || pickingUpData?.contactPhone.trim() === '') {
            Toast.show({
                content: 'No phone number available',
                position: 'bottom',
            });
            return;
        }

        // 清理电话号码，只保留数字和+号
        const cleanPhone = pickingUpData?.contactPhone.replace(/[^\d+]/g, '');

        if (!cleanPhone) {
            Toast.show({
                content: 'Invalid phone number format',
                position: 'bottom',
            });
            return;
        }

        try {
            // 尝试使用原生桥接
            window.location.href = `tel:${cleanPhone}`;
        } catch (error) {
            console.error('Phone call failed:', error);
            Toast.show({
                content: 'Phone call failed, please dial manually: ' + cleanPhone,
                position: 'bottom',
                duration: 3000
            });
        }
    };

    if (loading){
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>
                        {t('pages.pickUp.packageHandover.title')}
                    </NavBar>
                </div>
                <Skeleton.Title animated />
                <Skeleton.Paragraph lineCount={5} animated />
                <Skeleton.Title animated />
                <Skeleton.Paragraph lineCount={3} animated />
                <Skeleton.Title animated />
                <Skeleton.Paragraph lineCount={1} animated />
                <Skeleton style={{width: '100%',height: '30%'}} animated />
            </div>
        );
    }

    if (!pickingUpData) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>
                        {t('pages.pickUp.packageHandover.title')}
                    </NavBar>
                </div>
                <EmptyState/>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={() => navigate(-1)}>{t('pages.pickUp.packageHandover.title')}</NavBar>
            </div>

            <div className="flex-1 overflow-y-auto">
                {/* 站点信息 */}
                <div className="mb-1 bg-white rounded-lg p-4 shadow-sm">
                    <h2 className="text-base font-semibold mb-1">{t('pages.pickUp.packageHandover.siteInformation')}</h2>
                    <div className="mb-1">
                        <PickUpInfoItem label={`${t('pages.pickUp.packageHandover.name')}`} value={pickingUpData?.channelName} />
                        <PickUpInfoItem label={`${t('pages.pickUp.packageHandover.number')}`} value={t('pages.pickUp.end')} />
                        <PickUpInfoItem label={`${t('pages.pickUp.packageHandover.phone')}`} value={`${pickingUpData?.contactPhone}${pickingUpData?.ext ? `-${pickingUpData?.ext}` : ''}`} />
                        <PickUpInfoItem label={`${t('pages.pickUp.packageHandover.address')}`} value={buildAddressFromAppointment(pickingUpData)} />
                        <PickUpInfoItem label={`${t('pages.pickUp.packageHandover.appointmentTime')}`} value={`${pickingUpData?.appointmentTime ? formatTimestampByLanguage(pickingUpData?.appointmentTime, i18n.language) : pickingUpData?.appointmentTime}`} />
                    </div>
                </div>
                {/*上传图片*/}
                <div className="mb-2 bg-white rounded-lg p-4 shadow-sm">
                    <h2 className="text-base font-semibold mb-1">{t('pages.pickUp.packageHandover.photos')}</h2>
                    <p className="text-sm text-gray-600 mb-1">
                        {t('pages.pickUp.packageHandover.takeAtLeastPhotos')}
                    </p>
                    <div className="grid grid-cols-3 gap-2 my-4">
                        {PICKUP_FILE_PHOTO_SLOTS.map((slot) => (
                            <PickUpPhotoUploadCard
                                key={slot}
                                photoUrl={photos[slot]?.url}
                                type={slot}
                                description={``}
                                onTakePhoto={takePhoto}
                                onDelete={deletePhoto}
                                onView={handleViewPhoto}
                            />
                        ))}
                    </div>
                </div>

                {/* 查看大图 */}
                <PickUpPhotoViewer
                    photoUrl={viewingPhoto}
                    onClose={() => setViewingPhoto(null)}
                />
            </div>

            {/* 提交按钮 */}
            <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                <div className="flex-0">
                    <Button
                        shape="rounded"
                        className="w-12 h-12 flex items-center justify-center"
                        onClick={handleCall}
                        style={{'--background-color': '#f0f0f0'}}
                    >
                        <PhoneFill className="text-xl"/>
                    </Button>
                </div>
                <Button
                    block
                    color="primary"
                    loading={isLoading}
                    disabled={PICKUP_FILE_PHOTO_SLOTS.filter((slot) => photos[slot]?.url).length < MIN_HANDOVER_FILE_PHOTOS || isLoading}
                    onClick={handleCompletePackageHandover}
                >
                    {t('pages.pickUp.packageHandover.complete')}
                </Button>
            </div>
        </div>
    );
};

export default PackageHandoverPage;
